import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { callGeneratePattern } from '../../services/pattern.service';
import {
	trackError,
	trackPurchaseEvent,
	ANALYTICS_EVENTS,
	ERROR_CONTEXTS,
} from '../../services/analytics.service';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { PdfModal } from '../Main/Shared/PdfModal';
import { buttonTypographySx } from '../../styles/patterns';

/**
 * PatternResultPage Component
 *
 * This is the success page after Stripe checkout
 * Accessible via /pr?pid={patternId}&session_id={sessionId}
 *
 * Flow:
 * 1. Mount with query params
 * 2. Show spinner while calling CF
 * 3. CF verifies payment and generates PDF
 * 4. On success: redirect to download URL
 * 5. On 'not-yet-paid' error: retry with exponential backoff
 * 6. On other errors: show error message
 */
export const PatternResultPage = () => {
	const { user, loading: authLoading } = useAuth();
	const [searchParams] = useSearchParams();
	const patternId = searchParams.get('pid');
	const sessionId = searchParams.get('session_id');

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [retrying, setRetrying] = useState(false);
	const [pdfURL, setPdfURL] = useState(null);
	const [showModal, setShowModal] = useState(false);

	const retryWithBackoff = useCallback(async () => {
		const backoffDelays = [2000, 4000, 8000]; // 2s, 4s, 8s

		for (let i = 0; i < backoffDelays.length; i++) {
			const delay = backoffDelays[i];
			trackPurchaseEvent(ANALYTICS_EVENTS.PATTERN_NOT_YET_PAID_RETRY, {
				attempt_number: i + 1,
				delay_ms: delay,
				pattern_id: patternId,
			});

			setRetrying(true);
			setError(null);

			// Wait before retry
			await new Promise((resolve) => setTimeout(resolve, delay));

			try {
				const result = await callGeneratePattern(patternId, sessionId);

				if (result.downloadURL) {
					trackPurchaseEvent(
						ANALYTICS_EVENTS.PATTERN_GENERATION_SUCCEEDED,
						{ pattern_id: patternId, via_retry: true },
					);
					setPdfURL(result.downloadURL);
					setLoading(false);
					setRetrying(false);
					setShowModal(true);
					return;
				}
			} catch (err) {
				trackPurchaseEvent(ANALYTICS_EVENTS.PATTERN_GENERATION_RETRY, {
					attempt_number: i + 1,
					pattern_id: patternId,
					error_message: err.message,
				});

				// If not a 'not-yet-paid' error, stop retrying
				if (!err.message?.includes('not-yet-paid')) {
					trackError(ERROR_CONTEXTS.PATTERN_RESULT_RETRY, err, {
						attempt_number: i + 1,
						pattern_id: patternId,
					});
					setError(
						err.message ||
							'Failed to generate pattern. Please try again.',
					);
					setLoading(false);
					setRetrying(false);
					return;
				}

				// If this was the last retry, show error
				if (i === backoffDelays.length - 1) {
					trackError(
						ERROR_CONTEXTS.PATTERN_RESULT_RETRY_EXHAUSTED,
						err,
						{ pattern_id: patternId },
					);
					setError(
						'Payment verification is taking longer than expected. Please refresh the page or contact support.',
					);
					setLoading(false);
					setRetrying(false);
				}
			}
		}
	}, [patternId, sessionId]);

	const generateAndRedirect = useCallback(async () => {
		setLoading(true);
		setError(null);
		setRetrying(false);
		trackPurchaseEvent(ANALYTICS_EVENTS.PATTERN_RESULT_PAGE_LOADED, {
			pattern_id: patternId,
		});

		try {
			const result = await callGeneratePattern(patternId, sessionId);

			if (result.downloadURL) {
				trackPurchaseEvent(
					ANALYTICS_EVENTS.PATTERN_GENERATION_SUCCEEDED,
					{ pattern_id: patternId, via_retry: false },
				);
				setPdfURL(result.downloadURL);
				setLoading(false);
				setShowModal(true);
			}
		} catch (err) {
			// Handle specific error types
			if (
				err.code === 'functions/not-yet-paid' ||
				err.message?.includes('not-yet-paid')
			) {
				// Payment webhook not yet processed — retry with backoff
				retryWithBackoff();
			} else {
				// Other error — show to user
				trackError(ERROR_CONTEXTS.PATTERN_RESULT_GENERATE, err, {
					pattern_id: patternId,
				});
				setError(
					err.message ||
						'Failed to generate pattern. Please try again.',
				);
				setLoading(false);
			}
		}
	}, [patternId, sessionId, retryWithBackoff]);

	const handleRetry = () => {
		generateAndRedirect();
	};

	useEffect(() => {
		// Wait for auth to load before proceeding
		if (authLoading) return;
		// Don't call CF if user is not authenticated
		if (!user) {
			setError('You must be signed in to access this page');
			return;
		}
		if (!patternId || !sessionId) return;
		generateAndRedirect();
	}, [authLoading, patternId, sessionId, user, generateAndRedirect]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: '100vh',
				padding: 3,
				backgroundColor: '#f5f5f5',
			}}
		>
			{authLoading ? (
				<Box sx={{ textAlign: 'center' }}>
					<CircularProgress sx={{ marginBottom: 2 }} />
					<Typography variant="h6" gutterBottom>
						Loading...
					</Typography>
				</Box>
			) : loading || retrying ? (
				<Box sx={{ textAlign: 'center' }}>
					<CircularProgress sx={{ marginBottom: 2 }} />
					<Typography variant="h5" gutterBottom>
						{loading
							? 'Generating your pattern...'
							: 'Verifying payment...'}
					</Typography>
					<Typography variant="body1" color="textSecondary">
						This may take a moment. Please don't close this page.
					</Typography>
				</Box>
			) : error ? (
				<Box sx={{ textAlign: 'center', maxWidth: 500 }}>
					<Typography variant="h5" color="error" gutterBottom>
						Something went wrong
					</Typography>
					<Typography variant="body1" gutterBottom>
						{error}
					</Typography>
					<Button
						variant="contained"
						color="primary"
						onClick={handleRetry}
						sx={{ marginTop: 2 }}
					>
						Try Again
					</Button>
				</Box>
			) : pdfURL ? (
				<Box sx={{ textAlign: 'center' }}>
					<Typography variant="h5" gutterBottom>
						Your pattern is ready!
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="large"
						onClick={() => setShowModal(true)}
						sx={{ marginTop: 1 }}
					>
						<Typography variant="h5" sx={buttonTypographySx}>
							View Pattern
						</Typography>
					</Button>
				</Box>
			) : null}
			<PdfModal
				url={pdfURL}
				open={showModal}
				onClose={() => setShowModal(false)}
			/>
		</Box>
	);
};

export default PatternResultPage;
